import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, FileScan, Loader, Lock, Save } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { NumericInput, ScanTextInput } from '../../../common/inputs';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { invalidateDocInterpretationAvailability } from '../../../../hooks/useDocInterpretationAvailability';
import { docInterpretationService } from '../../../../services/docInterpretationService';
import type { DocInterpretationOverview, DocInterpretationSettings } from '../../../../types/docInterpretation';
import { usageLevel, usageBarWidth } from './docInterpretationAdminHelpers';

interface DocInterpretationSettingsPageProps {
  onBack?: () => void;
}

const fmtDate = (value: string | null) => (value ? new Date(value).toLocaleDateString('nb-NO') : '–');
const fmtPct = (value: number) => `${Math.round(value * 100)} %`;

/**
 * Innstillingssiden «Dokumenttolking» under Grunndata (TW 36423841, punkt 1–2 og 29).
 * Kundens administrator styrer handlingene og varslene. Aktivering, tak og AI-modell styres av
 * IntegrasjonsPartner og vises skrivebeskyttet for kunden.
 */
export const DocInterpretationSettingsPage: React.FC<DocInterpretationSettingsPageProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const p = 'manager.modules.docInterpretationSettings';

  const [settings, setSettings] = useState<DocInterpretationSettings | null>(null);
  const [overview, setOverview] = useState<DocInterpretationOverview | null>(null);
  const [capText, setCapText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [s, o] = await Promise.all([docInterpretationService.getSettings(), docInterpretationService.getOverview()]);
      setSettings(s);
      setCapText(s.monthlyCap != null ? String(s.monthlyCap) : '');
      setOverview(o);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (patch: Partial<DocInterpretationSettings>) => {
    setSaved(false);
    setSettings(prev => (prev ? { ...prev, ...patch } : prev));
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    try {
      const next = await docInterpretationService.saveSettings({
        ...settings,
        monthlyCap: capText.trim() === '' ? null : Number(capText),
      });
      setSettings(next);
      // Aktivering endrer om loggen og knappen «Les inn leverandørdokument» vises
      invalidateDocInterpretationAvailability();
      setCapText(next.monthlyCap != null ? String(next.monthlyCap) : '');
      setSaved(true);
      setOverview(await docInterpretationService.getOverview());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = (label: string, checked: boolean, onChange: (v: boolean) => void, disabled = false, help?: string) => (
    <label className={`flex items-start gap-3 py-2 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} className="w-5 h-5 mt-0.5" />
      <span>
        <span className="text-sm font-medium text-gray-800">{label}</span>
        {help && <span className="block text-xs text-gray-500">{help}</span>}
      </span>
    </label>
  );

  const level = overview ? usageLevel(overview.usagePercent) : 'none';
  const barClass = level === 'over' ? 'bg-red-600' : level === 'warning' ? 'bg-amber-500' : 'bg-blue-600';
  const canEditPlatform = settings?.canEditPlatformSettings ?? false;
  const templateText = (status: string) => t(`${p}.suppliers.template.${status}`, {}, status);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-6 pt-4 pb-2 bg-white border-b">
        <div className="flex items-center space-x-4">
          <BackButton onClick={onBack || (() => {})} />
          <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
            <FileScan size={24} />
            <span>{t(`${p}.title`, {}, 'Dokumenttolking')}</span>
          </h1>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-center gap-2">
            <AlertCircle size={16} />{error}
          </div>
        )}

        {!settings || !overview ? (
          <div className="flex items-center justify-center h-64"><Loader className="animate-spin" size={20} /></div>
        ) : (
          <>
            {/* Oversikt (punkt 29) */}
            <section className="p-5 bg-white border border-gray-200 rounded-lg space-y-3">
              <h2 className="text-lg font-semibold text-gray-800">
                {t(`${p}.overview.title`, { '0': `${String(overview.month).padStart(2, '0')}.${overview.year}` },
                  `Forbruk ${String(overview.month).padStart(2, '0')}.${overview.year}`)}
              </h2>
              <div className="text-3xl font-bold text-gray-800">
                {overview.documentCount}
                {overview.monthlyCap != null && <span className="text-lg font-normal text-gray-500"> / {overview.monthlyCap}</span>}
                {overview.usagePercent != null && <span className="ml-3 text-lg font-normal text-gray-600">({overview.usagePercent} %)</span>}
              </div>
              {overview.monthlyCap != null && overview.monthlyCap > 0 && (
                <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden" role="progressbar"
                  aria-valuenow={overview.documentCount} aria-valuemax={overview.monthlyCap}>
                  <div className={`h-full ${barClass}`} style={{ width: usageBarWidth(overview.usagePercent) }} />
                </div>
              )}
              {level === 'warning' && <div className="text-sm text-amber-800">{t(`${p}.overview.warning80`, {}, 'Over 80 % av taket er brukt.')}</div>}
              {level === 'over' && (
                <div className="text-sm text-red-800">
                  {t(`${p}.overview.overCap`, { '0': overview.overCapCount }, `Taket er nådd. ${overview.overCapCount} tolkinger over taket — tolkingen fortsetter.`)}
                </div>
              )}
              <div className="text-sm text-gray-600">
                {t(`${p}.overview.split`, { '0': overview.scriptCount, '1': overview.aiCount }, `Skript: ${overview.scriptCount} · AI: ${overview.aiCount}`)}
                {canEditPlatform && (
                  <span className="ml-2 text-gray-500">
                    · {t(`${p}.overview.cost`, { '0': overview.costAmount.toFixed(2), '1': overview.costCurrency }, `Kost ${overview.costAmount.toFixed(2)} ${overview.costCurrency}`)}
                  </span>
                )}
              </div>
            </section>

            {/* Handlinger og varsler (kundens administrator, punkt 2) */}
            <section className="p-5 bg-white border border-gray-200 rounded-lg space-y-2">
              <h2 className="text-lg font-semibold text-gray-800">{t(`${p}.actions.title`, {}, 'Handlinger ved OK')}</h2>
              {toggle(t(`${p}.actions.writeInboundShipmentNo`, {}, 'Skriv dokumentnummeret på bestillingslinjene'),
                settings.writeInboundShipmentNo, v => update({ writeInboundShipmentNo: v }), false,
                t(`${p}.actions.writeInboundShipmentNoHelp`, {}, 'Lageret kan da ta imot på dokumentnummeret.'))}
              {toggle(t(`${p}.actions.arrivalRegistrationDefault`, {}, 'Ankomstregistrer som standard'),
                settings.arrivalRegistrationDefault, v => update({ arrivalRegistrationDefault: v }), false,
                t(`${p}.actions.arrivalRegistrationDefaultHelp`, {}, 'Kan overstyres for hvert dokument.'))}
              {toggle(t(`${p}.actions.batchDistribution`, {}, 'Fordel batch på mottakslinjer'),
                settings.batchDistribution, v => update({ batchDistribution: v }), false,
                t(`${p}.actions.batchDistributionHelp`, {}, 'Én mottakslinje per batch. Linjer med batch ankomstregistreres alltid.'))}

              {/* Ankomstlokasjon per lager (TW 36423839) */}
              <div className="pt-4 space-y-2">
                <h3 className="font-semibold text-gray-800">{t(`${p}.arrivalBins.title`, {}, 'Ankomstlokasjon')}</h3>
                <p className="text-sm text-gray-600">
                  {t(`${p}.arrivalBins.help`, {}, 'Lokasjonen ankomstregistreringen får ved OK. OK er sperret for lagre uten lokasjon.')}
                </p>
                <table className="text-sm">
                  <tbody>
                    {settings.arrivalBins.map((row, index) => (
                      <tr key={row.warehouseId}>
                        <td className="pr-4 py-1 text-gray-700">{row.warehouseId}{row.warehouseName ? ` · ${row.warehouseName}` : ''}</td>
                        <td className="py-1">
                          <ScanTextInput value={row.binCode ?? ''} maxLength={30}
                            onChange={e => update({ arrivalBins: settings.arrivalBins.map((r, i) => (i === index ? { ...r, binCode: e.target.value } : r)) })}
                            placeholder={t(`${p}.arrivalBins.placeholder`, {}, 'Lokasjon')}
                            aria-label={t(`${p}.arrivalBins.binFor`, { '0': row.warehouseId }, `Ankomstlokasjon for lager ${row.warehouseId}`)}
                            className="w-40 px-3 py-2 border border-gray-300 rounded-lg" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h2 className="text-lg font-semibold text-gray-800 pt-4">{t(`${p}.notifications.title`, {}, 'Varsler')}</h2>
              {toggle(t(`${p}.notifications.notify80`, {}, 'Varsle ved 80 % av taket'), settings.notify80, v => update({ notify80: v }))}
              {toggle(t(`${p}.notifications.notify100`, {}, 'Varsle ved 100 % av taket'), settings.notify100, v => update({ notify100: v }))}
              <label className="block pt-2">
                <span className="text-sm font-medium text-gray-800">{t(`${p}.notifications.emailTo`, {}, 'E-post for varsler')}</span>
                <ScanTextInput value={settings.notifyEmailTo ?? ''} onChange={e => update({ notifyEmailTo: e.target.value })}
                  placeholder={t(`${p}.notifications.emailToPlaceholder`, {}, 'navn@firma.no, flere skilles med komma')}
                  className="mt-1 w-full max-w-lg px-3 py-2 border border-gray-300 rounded-lg" />
              </label>
            </section>

            {/* IntegrasjonsPartner (punkt 1) */}
            <section className="p-5 bg-white border border-gray-200 rounded-lg space-y-2">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                {!canEditPlatform && <Lock size={16} className="text-gray-400" />}
                {t(`${p}.platform.title`, {}, 'Avtale (styres av IntegrasjonsPartner)')}
              </h2>
              {!canEditPlatform && (
                <p className="text-xs text-gray-500">{t(`${p}.platform.readOnly`, {}, 'Disse feltene kan bare endres av IntegrasjonsPartner.')}</p>
              )}
              {toggle(t(`${p}.platform.enabled`, {}, 'Dokumenttolking er aktivert'), settings.enabled, v => update({ enabled: v }), !canEditPlatform)}
              <div className="flex flex-wrap gap-6 pt-2">
                <label className="block">
                  <span className="text-sm font-medium text-gray-800">{t(`${p}.platform.monthlyCap`, {}, 'Tak per måned (dokumenter)')}</span>
                  <NumericInput mode="integer" value={capText} disabled={!canEditPlatform}
                    onChange={e => { setSaved(false); setCapText(e.target.value); }}
                    className="mt-1 block w-40 px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100" />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-800">{t(`${p}.platform.aiModel`, {}, 'AI-modell')}</span>
                  <ScanTextInput value={settings.aiModel ?? ''} disabled={!canEditPlatform}
                    onChange={e => update({ aiModel: e.target.value })}
                    placeholder={t(`${p}.platform.aiModelDefault`, {}, 'Standard fra serveren')}
                    className="mt-1 block w-64 px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100" />
                </label>
              </div>
            </section>

            <div className="flex items-center gap-3">
              <button type="button" onClick={save} disabled={saving}
                className="px-5 py-3 bg-blue-600 text-white rounded-lg font-semibold flex items-center gap-2 hover:bg-blue-700 disabled:bg-gray-100 disabled:text-gray-400">
                {saving ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
                {t('common.buttons.save', {}, 'Lagre')}
              </button>
              {saved && <span className="text-sm text-green-800 flex items-center gap-1"><CheckCircle size={16} />{t(`${p}.saved`, {}, 'Lagret')}</span>}
            </div>

            {/* Leverandører og malstatus (punkt 29) */}
            <section className="p-5 bg-white border border-gray-200 rounded-lg">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">{t(`${p}.suppliers.title`, {}, 'Leverandører og malstatus')}</h2>
              {overview.suppliers.length === 0 ? (
                <p className="text-sm text-gray-500">{t(`${p}.suppliers.empty`, {}, 'Ingen dokumenter er tolket ennå.')}</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
                      <th className="py-2 pr-3">{t(`${p}.suppliers.supplier`, {}, 'Leverandør')}</th>
                      <th className="py-2 pr-3">{t(`${p}.suppliers.templateStatus`, {}, 'Malstatus')}</th>
                      <th className="py-2 pr-3 text-right">{t(`${p}.suppliers.documents`, {}, 'Dokumenter')}</th>
                      <th className="py-2 pr-3">{t(`${p}.suppliers.lastDocument`, {}, 'Siste dokument')}</th>
                      <th className="py-2 text-right">{t(`${p}.suppliers.correctionShare`, {}, 'Andel rettinger')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.suppliers.map(s => (
                      <tr key={s.supplierNo} className="border-b border-gray-200">
                        <td className="py-2 pr-3">{s.supplierName ?? s.supplierNo} <span className="text-gray-500">({s.supplierNo})</span></td>
                        <td className="py-2 pr-3">
                          {templateText(s.templateStatus)}
                          {s.templateStatus === 'Trial' && <span className="text-gray-500"> ({s.trialMatchCount}/{s.trialRequired})</span>}
                        </td>
                        <td className="py-2 pr-3 text-right">{s.documentCount}</td>
                        <td className="py-2 pr-3">{fmtDate(s.lastDocumentAt)}</td>
                        <td className="py-2 text-right">{fmtPct(s.correctionShare)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {/* Historikk per måned (punkt 29) */}
            <section className="p-5 bg-white border border-gray-200 rounded-lg">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">{t(`${p}.history.title`, {}, 'Historikk per måned')}</h2>
              {overview.history.length === 0 ? (
                <p className="text-sm text-gray-500">{t(`${p}.history.empty`, {}, 'Ingen tolkinger ennå.')}</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
                      <th className="py-2 pr-3">{t(`${p}.history.month`, {}, 'Måned')}</th>
                      <th className="py-2 pr-3 text-right">{t(`${p}.history.documents`, {}, 'Dokumenter')}</th>
                      <th className="py-2 pr-3 text-right">{t(`${p}.history.script`, {}, 'Skript')}</th>
                      <th className="py-2 pr-3 text-right">{t(`${p}.history.ai`, {}, 'AI')}</th>
                      <th className="py-2 pr-3 text-right">{t(`${p}.history.cap`, {}, 'Tak')}</th>
                      <th className="py-2 text-right">{t(`${p}.history.overCap`, {}, 'Over taket')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.history.map(h => (
                      <tr key={`${h.year}-${h.month}`} className="border-b border-gray-200">
                        <td className="py-2 pr-3">{String(h.month).padStart(2, '0')}.{h.year}</td>
                        <td className="py-2 pr-3 text-right">{h.documentCount}</td>
                        <td className="py-2 pr-3 text-right">{h.scriptCount}</td>
                        <td className="py-2 pr-3 text-right">{h.aiCount}</td>
                        <td className="py-2 pr-3 text-right">{h.monthlyCap ?? '–'}</td>
                        <td className={`py-2 text-right ${h.overCapCount > 0 ? 'text-red-800 font-semibold' : ''}`}>{h.overCapCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default DocInterpretationSettingsPage;
